import axios from "@/plugins/axios";
import utils from "./utils";
import { resolvePermissionCheck } from "@/utils/groupPermissions";

const state = {
    filters: {
        date_from: null,
        date_to: null,
        all_time: false,
        date_basis: "validated_date",
        buyer: null,
        assigned_to: null,
        manufacturer: null,
        tags: null,
        provider: null,
        product: null,
        include_services: false,
        billingaccount: null,
        establishment: null,
        pos_sync: null,
    },
    payload: null,
    loading: false,
    loading_errors: null,
    cancel_source: null,

    product_buyers: null,
    product_buyers_loading: false,
    product_buyers_errors: null,
    product_buyers_product_id: null,

    monthly_chart: null,
    monthly_chart_loading: false,
    monthly_chart_errors: null,
    monthly_chart_cancel_source: null,

    /** Last successful insights+chart session fingerprint (see `buildSalesInsightsSessionKey`). */
    lastInsightsSessionKey: null,
};

const mutations = {
    updateSalesInsightsFilters(state, filters) {
        state.filters = { ...state.filters, ...filters };
    },
    updateSalesInsightsPayload(state, payload) {
        state.payload = payload;
    },
    updateSalesInsightsLoading(state, v) {
        state.loading = v;
    },
    updateSalesInsightsLoadingErrors(state, errors) {
        state.loading_errors = errors;
    },
    updateSalesInsightsCancelSource(state, src) {
        state.cancel_source = src;
    },
    updateProductBuyers(state, data) {
        state.product_buyers = data;
    },
    updateProductBuyersLoading(state, v) {
        state.product_buyers_loading = v;
    },
    updateProductBuyersErrors(state, errors) {
        state.product_buyers_errors = errors;
    },
    updateProductBuyersProductId(state, id) {
        state.product_buyers_product_id = id;
    },
    updateMonthlyChart(state, payload) {
        state.monthly_chart = payload;
    },
    updateMonthlyChartLoading(state, v) {
        state.monthly_chart_loading = v;
    },
    updateMonthlyChartErrors(state, errors) {
        state.monthly_chart_errors = errors;
    },
    updateMonthlyChartCancelSource(state, src) {
        state.monthly_chart_cancel_source = src;
    },
    setLastInsightsSessionKey(state, key) {
        state.lastInsightsSessionKey = key;
    },
};

/** Stable JSON fingerprint of filters (aligned with `buildQueryParams` semantics). */
export function buildSalesInsightsFiltersFingerprint(filters) {
    const p = {};
    if (filters.all_time) {
        p.all_time = true;
    } else {
        if (filters.date_from) p.date_from = String(filters.date_from);
        if (filters.date_to) p.date_to = String(filters.date_to);
    }
    if (filters.date_basis) p.date_basis = filters.date_basis;
    if (filters.buyer != null && filters.buyer !== "") p.buyer = filters.buyer;
    if (filters.assigned_to != null && filters.assigned_to !== "") p.assigned_to = filters.assigned_to;
    if (filters.manufacturer != null && filters.manufacturer !== "") {
        const mf = Array.isArray(filters.manufacturer)
            ? filters.manufacturer.map((s) => String(s).trim()).filter(Boolean)
            : [String(filters.manufacturer).trim()].filter(Boolean);
        if (mf.length) p.manufacturer = mf;
    }
    if (filters.tags != null && filters.tags !== "") {
        const tags = Array.isArray(filters.tags)
            ? filters.tags.map((s) => String(s).trim()).filter(Boolean)
            : [String(filters.tags).trim()].filter(Boolean);
        if (tags.length) p.tags = tags;
    }
    if (filters.provider != null && filters.provider !== "") {
        p.provider = filters.provider;
    }
    if (filters.product != null && filters.product !== "") p.product = filters.product;
    if (filters.include_services) p.include_services = true;
    if (filters.billingaccount != null && filters.billingaccount !== "") {
        p.billingaccount = filters.billingaccount;
    }
    if (filters.establishment != null && filters.establishment !== "") {
        p.establishment = filters.establishment;
    }
    if (filters.pos_sync != null && filters.pos_sync !== "") {
        p.pos_sync = filters.pos_sync;
    }
    return JSON.stringify(p);
}

export function buildSalesInsightsChartFingerprint(chartOpts) {
    const resolution = chartOpts.resolution || "month";
    const group_by = chartOpts.group_by || "validated_date";
    const mode = chartOpts.mode || "aggregate";
    return JSON.stringify({ resolution, group_by, mode });
}

export function buildSalesInsightsSessionKey(filters, chartOpts) {
    return `${buildSalesInsightsFiltersFingerprint(filters)}|${buildSalesInsightsChartFingerprint(chartOpts)}`;
}

/**
 * @param {object} filters
 * @param {{ omitBuyer?: boolean }} [options] When `omitBuyer` is true, the buyer query param is omitted
 *   (used for product-buyers panel/CSV so the drawer lists all buyers for the product under the same criteria).
 */
function buildQueryParams(filters, options = {}) {
    const omitBuyer = options.omitBuyer === true;
    const p = {};
    if (filters.all_time) {
        p.all_time = "true";
    } else {
        if (filters.date_from) p.date_from = filters.date_from;
        if (filters.date_to) p.date_to = filters.date_to;
    }
    if (filters.date_basis) p.date_basis = filters.date_basis;
    if (filters.buyer && !omitBuyer) p.buyer = filters.buyer;
    if (filters.assigned_to) p.assigned_to = filters.assigned_to;
    if (filters.manufacturer) {
        const mf = Array.isArray(filters.manufacturer)
            ? filters.manufacturer.map((s) => String(s).trim()).filter(Boolean)
            : [String(filters.manufacturer).trim()].filter(Boolean);
        if (mf.length) p.manufacturer = mf;
    }
    if (filters.tags) {
        const tags = Array.isArray(filters.tags)
            ? filters.tags.map((s) => String(s).trim()).filter(Boolean)
            : [String(filters.tags).trim()].filter(Boolean);
        if (tags.length) p.tags = tags;
    }
    if (filters.provider != null && filters.provider !== "") {
        p.product_provider = filters.provider;
    }
    if (filters.product) p.product = filters.product;
    if (filters.include_services) p.include_services = "true";
    if (filters.billingaccount != null && filters.billingaccount !== "") {
        p.billingaccount = filters.billingaccount;
    }
    if (filters.establishment != null && filters.establishment !== "") {
        p.establishment = filters.establishment;
    }
    if (filters.pos_sync != null && filters.pos_sync !== "") {
        p.pos_sync = filters.pos_sync;
    }
    return p;
}

const actions = {
    async fetchSalesInsights({ commit, state }) {
        commit("updateSalesInsightsLoading", true);
        commit("updateSalesInsightsLoadingErrors", null);
        if (state.cancel_source) {
            state.cancel_source.cancel("canceled");
        }
        const cancelSource = axios.CancelToken.source();
        commit("updateSalesInsightsCancelSource", cancelSource);
        try {
            const response = await axios.get("/api/customerorderitems/sales-insights/", {
                params: buildQueryParams(state.filters),
                cancelToken: cancelSource.token,
            });
            commit("updateSalesInsightsPayload", response.data);
            commit("updateSalesInsightsLoading", false);
            commit("updateSalesInsightsCancelSource", null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit("updateSalesInsightsLoadingErrors", error.details);
            commit("updateSalesInsightsLoading", false);
            commit("updateSalesInsightsCancelSource", null);
            throw error;
        }
    },

    async fetchProductBuyers({ commit, state }, productId) {
        commit("updateProductBuyersLoading", true);
        commit("updateProductBuyersErrors", null);
        commit("updateProductBuyersProductId", productId);
        try {
            const params = { ...buildQueryParams(state.filters, { omitBuyer: true }), product: productId };
            const response = await axios.get("/api/customerorderitems/sales-insights/product-buyers/", {
                params,
            });
            commit("updateProductBuyers", response.data);
            commit("updateProductBuyersLoading", false);
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateProductBuyersErrors", error.details);
            commit("updateProductBuyersLoading", false);
            throw error;
        }
    },

    downloadSalesInsightsCsv({ state }) {
        return axios.get("/api/customerorderitems/sales-insights/", {
            params: buildQueryParams(state.filters),
            headers: { Accept: "text/csv" },
            responseType: "blob",
        });
    },

    downloadProductBuyersCsv({ state }, productId) {
        const params = { ...buildQueryParams(state.filters, { omitBuyer: true }), product: productId };
        return axios.get("/api/customerorderitems/sales-insights/product-buyers/", {
            params,
            headers: { Accept: "text/csv" },
            responseType: "blob",
        });
    },

    async fetchSalesInsightsMonthlyChart({ commit, state, rootGetters }, chart) {
        const permissions = rootGetters["session/current_user_permissions"];
        if (!resolvePermissionCheck(permissions, "order.view_order")) {
            return;
        }
        const chartOpts = chart || {};
        const resolution = chartOpts.resolution || "month";
        const groupBy = chartOpts.group_by || "validated_date";
        const mode = chartOpts.mode || "aggregate";

        commit("updateMonthlyChartLoading", true);
        commit("updateMonthlyChartErrors", null);
        if (state.monthly_chart_cancel_source) {
            state.monthly_chart_cancel_source.cancel("canceled");
        }
        const cancelSource = axios.CancelToken.source();
        commit("updateMonthlyChartCancelSource", cancelSource);

        const f = state.filters;
        const toIso = (d) => d.toISOString().slice(0, 10);

        const params = {
            resolution,
            group_by: groupBy,
            chart_mode: mode,
        };
        if (f.buyer != null && f.buyer !== "") {
            params.buyer = f.buyer;
        }
        if (f.assigned_to != null && f.assigned_to !== "") {
            params.assigned_to = f.assigned_to;
        }
        if (f.manufacturer != null && f.manufacturer !== "") {
            const mf = Array.isArray(f.manufacturer)
                ? f.manufacturer.map((s) => String(s).trim()).filter(Boolean)
                : [String(f.manufacturer).trim()].filter(Boolean);
            if (mf.length) params.manufacturer = mf;
        }
        if (f.tags != null && f.tags !== "") {
            const tags = Array.isArray(f.tags)
                ? f.tags.map((s) => String(s).trim()).filter(Boolean)
                : [String(f.tags).trim()].filter(Boolean);
            if (tags.length) params.tags = tags;
        }
        if (f.provider != null && f.provider !== "") {
            params.product_provider = f.provider;
        }
        if (f.billingaccount != null && f.billingaccount !== "") {
            params.billingaccount = f.billingaccount;
        }
        params.ready = true;
        params.exclude_canceled = true;
        if (!f.all_time) {
            let chartDateFrom = f.date_from;
            let chartDateTo = f.date_to;
            if (!chartDateFrom || !chartDateTo) {
                const end = new Date();
                const start = new Date();
                start.setDate(end.getDate() - 90);
                chartDateFrom = toIso(start);
                chartDateTo = toIso(end);
            }
            params[`${groupBy}_min`] = chartDateFrom;
            params[`${groupBy}_max`] = chartDateTo;
        }

        try {
            const response = await axios.get("/api/customerorders/stats/monthly/", {
                params,
                cancelToken: cancelSource.token,
            });
            commit("updateMonthlyChart", response.data);
            commit("updateMonthlyChartLoading", false);
            commit("updateMonthlyChartCancelSource", null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit("updateMonthlyChartErrors", error.details);
            commit("updateMonthlyChartLoading", false);
            commit("updateMonthlyChartCancelSource", null);
            throw error;
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
};
